import React, { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { PageHeader } from '../components/common'
import {
  Button,
  useErrorService,
  useUserService,
  useNotificationService,
} from '@abroad/components'
import { ShimmerThumbnail } from 'react-shimmer-effects'
import API from '../utils/API'
import { Row } from 'react-bootstrap'
import { labels as formLabels, getAddressFieldLabel } from '../constants/form'
import classNames from 'classnames'
import SimpleDatePicker from '../components/form/SimpleDatePicker'
import FormCheckbox from '../components/form/FormCheckbox'
import SubQuestionsDisplay from '../components/form/SubQuestionsDisplay'
import CountryInput from '../components/form/CountryInput'
import PhoneInput from '../components/form/PhoneInput'
import { renderTextWithLinks } from '../utils/textUtils'
import { validateField } from '../utils/formValidation'
import {
  getCountryByDialCode,
  getProcessedCountryData,
} from '../utils/countryData'
import ReactCountryFlag from 'react-country-flag'

const EnrollTypeFormAnswer = () => {
  const [questTitle, setQuestTitle] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [formData, setFormData] = useState({})
  const [isEditable, setIsEditable] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [editableFormData, setEditableFormData] = useState({})
  const [validationErrors, setValidationErrors] = useState({})
  const history = useHistory()
  const { questId, questCustomFormId } = useParams()
  const { user } = useUserService()
  const Error = useErrorService()
  const Notification = useNotificationService()

  // Helper function to check if a dependent question should be shown
  const shouldShowDependentQuestion = (question, currentFormData) => {
    if (question.questionType !== 'dependent') {
      return true
    }

    // Find the parent question from formData.answers
    const parentQuestion = formData.answers?.find(
      (answer) => answer.questionId === question.dependentQuestionId,
    )
    if (!parentQuestion) {
      return false
    }

    // Get the parent question's answer - use current edited values if in editing mode, otherwise use saved answers
    let parentAnswerData = parentQuestion

    if (isEditable) {
      // When editing, check if there's a current edit for this question
      const editKey = parentQuestion.questionId
      const currentEdit = editableFormData[editKey]

      if (currentEdit !== undefined) {
        // Use the current edit value
        if (parentQuestion.answerType === 'multipleSelectionDropdown') {
          parentAnswerData = { ...parentAnswerData, answer: currentEdit }
        } else {
          // Handle null values (no selection) for single selection dropdowns
          if (currentEdit === null || currentEdit === '') {
            parentAnswerData = { ...parentAnswerData, answer: [] }
          } else {
            parentAnswerData = { ...parentAnswerData, answer: [currentEdit] }
          }
        }
      }
    }

    // Extract the parent answer value
    let parentAnswer
    if (parentAnswerData.subAnswers && parentAnswerData.subAnswers.length > 0) {
      // For object type questions, we need to check if any sub-answer matches
      parentAnswer = parentAnswerData.subAnswers
        .map((sub) => sub.answer[0])
        .filter(Boolean)
    } else {
      parentAnswer = parentAnswerData.answer
    }

    if (
      !parentAnswer ||
      (Array.isArray(parentAnswer) && parentAnswer.length === 0)
    ) {
      return false
    }

    // Check if the parent answer matches any of the conditions
    const conditions = question.condition || []
    if (conditions.length === 0) {
      return false
    }

    // Handle different answer types for parent question
    if (parentQuestion.answerType === 'multipleSelectionDropdown') {
      // For multiple selection, check if ANY of the selected options match ANY of the conditions
      if (Array.isArray(parentAnswer)) {
        return parentAnswer.some((selectedOption) => {
          // Find the option ID for the selected option text
          const selectedOptionObj = parentQuestion.options?.find(
            (opt) => opt.option === selectedOption,
          )
          const selectedOptionId =
            selectedOptionObj?._id || selectedOptionObj?.id
          return conditions.some((condition) => selectedOptionId === condition)
        })
      } else {
        return false
      }
    } else {
      // For single selection, check if the answer matches any of the conditions
      const answerValue = Array.isArray(parentAnswer)
        ? parentAnswer[0]
        : parentAnswer

      // Check if the answer is in the new format with ID (text|id)
      let selectedOptionId
      if (typeof answerValue === 'string' && answerValue.includes('|')) {
        // New format: extract the ID from "text|id"
        selectedOptionId = answerValue.split('|')[1]
      } else {
        // Legacy format: find the option ID for the selected option text
        const selectedOptionObj = parentQuestion.options?.find(
          (opt) => opt.option === answerValue,
        )
        selectedOptionId = selectedOptionObj?._id || selectedOptionObj?.id
      }

      // If we couldn't find the option ID, try to match the condition directly with the answer value
      if (!selectedOptionId) {
        // This handles cases where the condition might be the option text itself
        return conditions.some((condition) => answerValue === condition)
      }

      const result = conditions.some(
        (condition) => selectedOptionId === condition,
      )

      return result
    }
  }

  // Helper function to filter answers based on dependent logic
  const getFilteredAnswers = (answers) => {
    if (!answers) return answers

    return answers.filter((answer) => {
      // For dependent questions, check if they should be shown based on condition
      if (answer.questionType === 'dependent') {
        // First check if the dependent question should be shown based on parent condition
        const shouldShowBasedOnCondition = shouldShowDependentQuestion(
          answer,
          editableFormData,
        )

        // For deleted dependent questions, also check if they have answers
        if (answer.isDeleted) {
          const hasAnswers =
            (answer.answer &&
              answer.answer.length > 0 &&
              answer.answer.some((item) => item && item.trim() !== '')) ||
            (answer.subAnswers &&
              answer.subAnswers.some(
                (sub) =>
                  sub.answer &&
                  sub.answer.length > 0 &&
                  sub.answer.some((item) => item && item.trim() !== ''),
              ))
          return shouldShowBasedOnCondition && hasAnswers
        } else {
          // For non-deleted dependent questions, show if they meet the condition
          return shouldShowBasedOnCondition
        }
      }

      // For non-dependent questions, show them normally
      return true
    })
  }

  // Add state to store filtered answers
  const [filteredAnswers, setFilteredAnswers] = useState([])

  // Update filtered answers when formData or editableFormData changes
  useEffect(() => {
    if (formData?.answers) {
      const filtered = getFilteredAnswers(formData.answers)
      setFilteredAnswers(filtered)
    }
  }, [formData?.answers, editableFormData, isEditable])

  useEffect(() => {
    const fetchQuestTitle = async () => {
      try {
        const { data } = await API.quests.allQuest()
        if (data) {
          const quest = data.find((q) => q._id === questId)
          setQuestTitle(quest?.title || '')
        }
      } catch (e) {
        Error.showError(e)
      }
    }

    const fetchFormAnswers = async () => {
      try {
        const { data } = await API.quests.getCustomFormAnswersOfUser(
          questId,
          user._id,
          questCustomFormId,
        )
        if (data) {
          setFormData(data)
        }
      } catch (e) {
        Error.showError(e)
      }
    }

    const fetchData = async () => {
      if (questId && questCustomFormId && user?._id) {
        setIsLoading(true)
        await Promise.all([fetchQuestTitle(), fetchFormAnswers()])
        setIsLoading(false)
      } else {
        setIsLoading(false)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, questCustomFormId, user?._id])

  // Auto-resize textarea elements
  useEffect(() => {
    if (!isLoading && formData?.answers) {
      const textareas = document.querySelectorAll(
        '.form-textarea-display.form-control',
      )
      textareas.forEach((textarea) => {
        // Only auto-resize when NOT editing (read-only mode)
        if (textarea.disabled) {
          // Reset height to auto to get the correct scrollHeight
          textarea.style.height = 'auto'
          // For read-only textareas, set height based on content without minimum
          const newHeight = textarea.scrollHeight
          textarea.style.height = newHeight + 'px'
          textarea.style.overflow = 'hidden'
          textarea.style.resize = 'none'
        }
        // For editable textareas, let them handle their own resizing
      })
    }
  }, [isLoading, formData, editableFormData, isEditable])

  // Initialize editable form data when formData changes
  useEffect(() => {
    if (formData?.answers) {
      const initialEditableData = {}
      formData.answers.forEach((answer, index) => {
        // Use only questionId as key to match API payload structure
        const key = answer.questionId

        // Handle object type questions differently
        if (answer.subAnswers && answer.subAnswers.length > 0) {
          // For object type questions, extract values from subAnswers
          const objectData = {}
          answer.subAnswers.forEach((subAnswer) => {
            const subValue = Array.isArray(subAnswer.answer)
              ? subAnswer.answer[0]
              : subAnswer.answer
            // Use subQuestionId directly without index
            const subKey = subAnswer.subQuestionId
            if (subAnswer.subQuestionId) {
              objectData[subKey] = subValue || ''
            }
          })
          initialEditableData[key] = objectData
        } else {
          // For regular questions, extract the value from answer
          let answerValue = answer.answer

          // Handle different question types properly
          if (answer.answerType === 'multipleSelectionDropdown') {
            // For multiple selection dropdowns, preserve the entire array
            initialEditableData[key] = Array.isArray(answerValue)
              ? answerValue
              : []
          } else if (answer.answerType === 'singleSelectionDropdown') {
            // For single selection dropdowns, extract the first value from array
            let extractedValue
            if (Array.isArray(answerValue)) {
              extractedValue = answerValue[0] || ''
            } else {
              extractedValue = answerValue || ''
            }

            // If the extracted value is empty string, set it to null to indicate no selection
            if (extractedValue === '') {
              initialEditableData[key] = null
            } else if (extractedValue && answer.options) {
              // For existing data, we need to create the new format with ID
              const selectedOption = answer.options.find((opt) => {
                const optText = typeof opt === 'object' ? opt.option : opt
                return optText === extractedValue
              })
              const selectedOptionId = selectedOption?._id || selectedOption?.id
              initialEditableData[key] = selectedOptionId
                ? `${extractedValue}|${selectedOptionId}`
                : extractedValue
            } else {
              initialEditableData[key] = extractedValue
            }
          } else if (answer.answerType === 'date') {
            // For date fields, extract the first value from array
            if (Array.isArray(answerValue)) {
              initialEditableData[key] = answerValue[0] || ''
            } else {
              initialEditableData[key] = answerValue || ''
            }
          } else if (answer.answerType === 'scale') {
            // For scale fields, extract the first value from array
            if (Array.isArray(answerValue)) {
              initialEditableData[key] = answerValue[0] || ''
            } else {
              initialEditableData[key] = answerValue || ''
            }
          } else {
            // For other question types (text, email, phone), extract the string value from answer
            if (Array.isArray(answerValue)) {
              initialEditableData[key] = answerValue[0] || ''
            } else {
              initialEditableData[key] = answerValue || ''
            }
          }
        }
      })
      setEditableFormData(initialEditableData)
    }
  }, [formData])

  const handleFieldChange = (questionId, value) => {
    setEditableFormData((prev) => {
      const newData = {
        ...prev,
        [questionId]: value,
      }

      // Clear dependent question values when parent question changes
      if (formData?.answers) {
        // Find dependent questions that depend on this question
        const dependentQuestions = formData.answers.filter(
          (answer) =>
            answer.questionType === 'dependent' &&
            answer.dependentQuestionId === questionId,
        )

        // Clear values for dependent questions that should be hidden
        dependentQuestions.forEach((answer) => {
          const conditions = answer.condition || []
          let shouldShow = false

          if (conditions.length > 0) {
            // Find the parent question to determine its answer type
            const parentQuestion = formData.answers.find(
              (a) => a.questionId === questionId,
            )

            if (
              parentQuestion &&
              parentQuestion.answerType === 'multipleSelectionDropdown'
            ) {
              // For multiple selection, check if ANY of the selected options match ANY of the conditions
              shouldShow =
                Array.isArray(value) &&
                value.some((selectedOption) => {
                  // Find the option ID for the selected option text
                  const selectedOptionObj = parentQuestion.options?.find(
                    (opt) => opt.option === selectedOption,
                  )
                  const selectedOptionId =
                    selectedOptionObj?._id || selectedOptionObj?.id
                  return conditions.some(
                    (condition) => selectedOptionId === condition,
                  )
                })
            } else {
              // For single selection, check if the answer matches any of the conditions
              const selectedOptionObj = parentQuestion.options?.find(
                (opt) => opt.option === value,
              )
              const selectedOptionId =
                selectedOptionObj?._id || selectedOptionObj?.id
              shouldShow = conditions.some(
                (condition) => selectedOptionId === condition,
              )
            }
          }

          if (!shouldShow) {
            // Clear the dependent question value
            if (answer.answerType === 'object') {
              // For object type questions, clear all sub-answers
              const clearedObject = {}
              if (answer.subAnswers) {
                answer.subAnswers.forEach((subAnswer) => {
                  clearedObject[subAnswer.subQuestionId] = ''
                })
              }
              newData[answer.questionId] = clearedObject
            } else if (answer.answerType === 'multipleSelectionDropdown') {
              // For multiple selection, clear to empty array
              newData[answer.questionId] = []
            } else {
              // For other types, clear to empty string
              newData[answer.questionId] = ''
            }
          }
        })
      }

      return newData
    })

    // Clear validation errors for this field when user starts typing
    if (validationErrors[questionId]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[questionId]
        return newErrors
      })
    }
  }

  const handleCancel = () => {
    // Revert editable form data to original values
    const originalData = {}
    formData.answers.forEach((answer, index) => {
      // Use only questionId as key to match API payload structure
      const key = answer.questionId

      // Handle object type questions differently
      if (answer.subAnswers && answer.subAnswers.length > 0) {
        // For object type questions, extract values from subAnswers
        const objectData = {}
        answer.subAnswers.forEach((subAnswer) => {
          const subValue = Array.isArray(subAnswer.answer)
            ? subAnswer.answer[0]
            : subAnswer.answer
          // Use subQuestionId directly without index
          const subKey = subAnswer.subQuestionId
          if (subAnswer.subQuestionId) {
            objectData[subKey] = subValue || ''
          }
        })
        originalData[key] = objectData
      } else {
        // For regular questions, extract the value from answer
        let answerValue = answer.answer

        // Handle different question types properly
        if (answer.answerType === 'multipleSelectionDropdown') {
          // For multiple selection dropdowns, preserve the entire array
          originalData[key] = Array.isArray(answerValue) ? answerValue : []
        } else if (answer.answerType === 'singleSelectionDropdown') {
          // For single selection dropdowns, extract the first value from array
          let extractedValue
          if (Array.isArray(answerValue)) {
            extractedValue = answerValue[0] || ''
          } else {
            extractedValue = answerValue || ''
          }

          // For existing data, we need to create the new format with ID
          if (extractedValue && answer.options) {
            const selectedOption = answer.options.find((opt) => {
              const optText = typeof opt === 'object' ? opt.option : opt
              return optText === extractedValue
            })
            const selectedOptionId = selectedOption?._id || selectedOption?.id
            originalData[key] = selectedOptionId
              ? `${extractedValue}|${selectedOptionId}`
              : extractedValue
          } else {
            originalData[key] = extractedValue
          }
        } else if (answer.answerType === 'date') {
          // For date fields, extract the first value from array
          if (Array.isArray(answerValue)) {
            originalData[key] = answerValue[0] || ''
          } else {
            originalData[key] = answerValue || ''
          }
        } else if (answer.answerType === 'scale') {
          // For scale fields, extract the first value from array
          if (Array.isArray(answerValue)) {
            originalData[key] = answerValue[0] || ''
          } else {
            originalData[key] = answerValue || ''
          }
        } else {
          // For other question types (text, email, phone), extract the string value from answer
          if (Array.isArray(answerValue)) {
            originalData[key] = answerValue[0] || ''
          } else {
            originalData[key] = answerValue || ''
          }
        }
      }
    })
    setEditableFormData(originalData)
    setValidationErrors({}) // Clear validation errors
    setIsEditable(false)
  }

  // Validation functions are now imported from '../utils/formValidation'
  // Using: validateEmail, validatePhone, validateCountry, validateRequired, validateField

  const validateForm = () => {
    const errors = {}

    for (const answer of formData.answers) {
      const fieldKey = answer.questionId
      let value = editableFormData[fieldKey]

      // Skip file type questions
      if (answer.answerType === 'files') {
        continue
      }

      // Skip validation for dependent questions that should be hidden
      if (answer.questionType === 'dependent') {
        // Check if dependent question should be shown based on current form state
        const shouldShow = shouldShowDependentQuestion(answer, editableFormData)

        if (!shouldShow) {
          // For hidden dependent questions, skip validation entirely
          continue
        }
      }

      // For dependent questions, if the value is not in editableFormData, use the original answer
      if (answer.questionType === 'dependent' && value === undefined) {
        if (answer.answerType === 'object') {
          // For object type questions, extract values from subAnswers
          const objectData = {}
          if (answer.subAnswers) {
            for (const subAnswer of answer.subAnswers) {
              const subValue = Array.isArray(subAnswer.answer)
                ? subAnswer.answer[0]
                : subAnswer.answer
              const subKey = subAnswer.subQuestionId
              if (subAnswer.subQuestionId) {
                objectData[subKey] = subValue || ''
              }
            }
          }
          value = objectData
        } else {
          // For regular questions, extract the value from answer
          let answerValue = answer.answer
          if (Array.isArray(answerValue)) {
            value = answerValue[0] || ''
          } else {
            value = answerValue || ''
          }
        }
      }

      // Use the answer object itself as the question definition
      const fieldErrors = validateField(fieldKey, value, answer, answer)
      if (fieldErrors) {
        errors[fieldKey] = fieldErrors
      }
    }

    return errors
  }

  const renderLabel = (question, isRequired = false, links = []) => {
    if (!question) return ''
    const baseLabel =
      getAddressFieldLabel(question) || formLabels[question] || question
    const renderedText = renderTextWithLinks(baseLabel, links)

    // Show asterisk (*) when editing and question is required
    if (isEditable && isRequired) {
      return (
        <div className='d-flex align-items-center'>
          <span>{renderedText}</span>
          <span className='required-question line-height-none mt-1 ms-1'>
            *
          </span>
        </div>
      )
    }
    return renderedText
  }

  const onSubmit = async () => {
    try {
      // Validate form before submission
      const errors = validateForm()

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors)

        // Collect all specific error messages
        const allErrorMessages = Object.values(errors).flat()
        const errorMessage =
          allErrorMessages.length > 0
            ? allErrorMessages[0]
            : 'Please complete required field.'

        Notification.showNotification(errorMessage, 'danger')
        return
      }

      setIsUpdating(true)

      // Transform editable form data back to API format
      const updatedAnswers = formData.answers
        .map((answer, index) => {
          // Use only questionId as key to match API payload structure
          const fieldKey = answer.questionId
          const updatedValue = editableFormData[fieldKey]

          // Skip file type questions - don't send them in the update
          if (answer.answerType === 'files') {
            return null
          }

          // Check if this is a dependent question that should be hidden
          if (answer.questionType === 'dependent') {
            const shouldShow = shouldShowDependentQuestion(
              answer,
              editableFormData,
            )
            if (!shouldShow) {
              // Send empty array for hidden dependent questions
              if (answer.answerType === 'object') {
                // For object type questions, send empty subAnswers
                const emptySubAnswers =
                  answer.subAnswers?.map((subAnswer) => ({
                    subQuestionId: subAnswer.subQuestionId,
                    answer: [],
                  })) || []

                return {
                  questionId: answer.questionId,
                  subAnswers: emptySubAnswers,
                }
              } else {
                // For other question types, send empty answer array
                return {
                  questionId: answer.questionId,
                  answer: [],
                }
              }
            }
          }

          // Handle deleted questions - use the original answer structure
          if (answer.isDeleted) {
            // For deleted questions, preserve the original structure
            if (answer.subAnswers && answer.subAnswers.length > 0) {
              // For object type deleted questions
              const updatedSubAnswers = answer.subAnswers.map((subAnswer) => {
                const subKey = subAnswer.subQuestionId // Use subQuestionId directly without index
                const subValue =
                  updatedValue && updatedValue[subKey]
                    ? updatedValue[subKey]
                    : ''

                return {
                  subQuestionId: subAnswer.subQuestionId,
                  answer: subValue && subValue.trim() !== '' ? [subValue] : [],
                }
              })

              return {
                questionId: answer.questionId,
                subAnswers: updatedSubAnswers,
              }
            } else {
              // For regular deleted questions
              let formattedAnswer = null

              if (updatedValue !== undefined && updatedValue !== null) {
                if (Array.isArray(updatedValue)) {
                  formattedAnswer = updatedValue
                } else if (typeof updatedValue === 'string') {
                  if (updatedValue.trim() === '') {
                    formattedAnswer = []
                  } else {
                    formattedAnswer = [updatedValue]
                  }
                } else if (
                  updatedValue !== null &&
                  updatedValue !== undefined
                ) {
                  formattedAnswer = [updatedValue.toString()]
                }
              }

              return {
                questionId: answer.questionId,
                answer: formattedAnswer || [],
              }
            }
          }

          if (answer.answerType === 'object') {
            // For object type questions, use subAnswers structure
            const subAnswers = answer.subAnswers.map((subAnswer) => ({
              subQuestionId: subAnswer.subQuestionId, // Use subQuestionId
              answer:
                updatedValue &&
                updatedValue[subAnswer.subQuestionId] &&
                updatedValue[subAnswer.subQuestionId].trim() !== ''
                  ? [updatedValue[subAnswer.subQuestionId]]
                  : [],
            }))

            return {
              questionId: answer.questionId, // Use original answer.questionId instead of questionDefinition._id
              subAnswers: subAnswers,
            }
          } else {
            // For all other question types, use answer structure
            let formattedAnswer = []

            // Handle different question types appropriately
            if (
              answer.answerType === 'singleSelectionDropdown' ||
              answer.answerType === 'multipleSelectionDropdown'
            ) {
              // For single and multiple selection questions, always send an array
              if (updatedValue !== undefined && updatedValue !== null) {
                if (Array.isArray(updatedValue)) {
                  // For multiple selection questions, keep as array
                  formattedAnswer = updatedValue
                } else if (
                  typeof updatedValue === 'string' &&
                  updatedValue.trim() !== ''
                ) {
                  // For single selection questions, wrap in array
                  // For single selection dropdowns, extract just the text part if it's in new format
                  let valueToSend = updatedValue
                  if (
                    answer.answerType === 'singleSelectionDropdown' &&
                    updatedValue.includes('|')
                  ) {
                    valueToSend = updatedValue.split('|')[0] // Extract just the text part
                  }
                  formattedAnswer = [valueToSend]
                } else if (
                  updatedValue !== null &&
                  updatedValue !== undefined
                ) {
                  // For any other non-null values
                  formattedAnswer = [updatedValue.toString()]
                } else {
                  // For null/undefined values, send empty array
                  formattedAnswer = []
                }
              } else {
                // If updatedValue is null/undefined, send empty array
                formattedAnswer = []
              }
            } else {
              // For other question types (text, email, phone, date, scale, files)
              if (updatedValue !== undefined && updatedValue !== null) {
                if (Array.isArray(updatedValue)) {
                  formattedAnswer = updatedValue
                } else if (typeof updatedValue === 'string') {
                  // Send empty array if string is empty, otherwise send array with value
                  if (updatedValue.trim() === '') {
                    formattedAnswer = []
                  } else {
                    formattedAnswer = [updatedValue]
                  }
                } else if (
                  updatedValue !== null &&
                  updatedValue !== undefined
                ) {
                  // For any other non-null values
                  formattedAnswer = [updatedValue.toString()]
                }
              }
              // If no answer provided, formattedAnswer remains empty array []
            }

            return {
              questionId: answer.questionId, // Use original answer.questionId instead of questionDefinition._id
              answer: formattedAnswer,
            }
          }
        })
        .filter(Boolean) // Remove null entries (file type questions)

      // Only send the answers array, not the entire formData object
      const payload = {
        answers: updatedAnswers,
      }

      const { status } = await API.quests.updateCustomFormAnswers(
        questId,
        questCustomFormId,
        payload,
      )
      if (status) {
        // Immediately update the form data with the new answers to prevent flash
        const updatedFormData = {
          ...formData,
          answers: updatedAnswers,
        }
        setFormData(updatedFormData)

        // Refresh the form data by calling the API again to get the latest data
        try {
          const { data } = await API.quests.getCustomFormAnswersOfUser(
            questId,
            user._id,
            questCustomFormId,
          )
          if (data) {
            setFormData(data)
          }
        } catch (refreshError) {
          // If refresh fails, we already have the updated data from above
          console.warn('Failed to refresh form data:', refreshError)
        }

        // Only set updating to false and exit edit mode after refresh is complete
        setIsUpdating(false)
        setIsEditable(false)

        Notification.showNotification(
          'Form answers have been updated!',
          'success',
        )
      }
    } catch (e) {
      setIsUpdating(false)
      setIsEditable(true)
      Error.showError(e)
    }
  }

  const renderField = (answer, index) => {
    const {
      question,
      answer: answerValue,
      subAnswers,
      questionId,
      answerType,
      isRequired,
      isDeleted,
      options,
      scaleOptions,
      links,
    } = answer

    // Skip rendering if question is undefined
    if (!question) {
      return null
    }

    // Use only questionId as key to match API payload structure
    const fieldKey = questionId

    // When editing, use editableFormData value, otherwise use original answerValue
    const currentValue = isEditable
      ? editableFormData[fieldKey] !== undefined
        ? editableFormData[fieldKey]
        : (() => {
            // Fallback: extract value from original answerValue if editableFormData doesn't have it
            let fallbackValue = answerValue

            if (answerType === 'multipleSelectionDropdown') {
              return Array.isArray(fallbackValue) ? fallbackValue : []
            } else if (
              answerType === 'singleSelectionDropdown' ||
              answerType === 'date' ||
              answerType === 'scale'
            ) {
              return Array.isArray(fallbackValue)
                ? fallbackValue[0] || ''
                : fallbackValue || ''
            } else {
              return Array.isArray(fallbackValue)
                ? fallbackValue[0] || ''
                : fallbackValue || ''
            }
          })()
      : answerValue

    // For single selection dropdowns, extract the first value from array
    let displayValue
    if (answerType === 'singleSelectionDropdown') {
      let extractedValue = Array.isArray(currentValue)
        ? currentValue[0] || ''
        : currentValue || ''

      // Handle null values (no selection)
      if (extractedValue === null || extractedValue === undefined) {
        displayValue = ''
      } else if (
        typeof extractedValue === 'string' &&
        extractedValue.includes('|')
      ) {
        // Handle new format with ID (text|id)
        displayValue = extractedValue.split('|')[0] // Extract just the text part
      } else {
        displayValue = extractedValue
      }
    } else {
      displayValue = Array.isArray(currentValue)
        ? currentValue.join(', ')
        : currentValue
    }

    // Helper function to check if answer has value
    const hasValue = (value) => {
      if (!value) return false
      if (Array.isArray(value)) {
        return (
          value.length > 0 && value.some((item) => item && item.trim() !== '')
        )
      }
      if (typeof value === 'string') {
        return value.trim() !== ''
      }
      if (typeof value === 'object') {
        // For object type values, check if any property has a value
        return Object.values(value).some((val) => {
          if (typeof val === 'string') {
            return val.trim() !== ''
          }
          return val && val !== ''
        })
      }
      return value !== '' && value !== null && value !== undefined
    }

    // Handle sub-questions (like Home Address)
    if (subAnswers && subAnswers.length > 0) {
      // Show all object type questions regardless of values
      // For deleted questions, still show them if they have answers
      if (isDeleted) {
        const hasAnySubAnswer = subAnswers.some((subAnswer) =>
          hasValue(subAnswer.answer),
        )
        if (!hasAnySubAnswer) {
          return null
        }
      }

      // Get the current editable data for this object type question
      const currentObjectData = editableFormData[fieldKey] || {}

      return (
        <SubQuestionsDisplay
          key={fieldKey}
          label={question}
          subAnswers={subAnswers}
          isEditable={isEditable}
          currentObjectData={currentObjectData}
          isRequired={isRequired}
          validationErrors={validationErrors[fieldKey] || []}
          questionDefinition={answer} // Pass the answer object as questionDefinition
          links={links}
          onFieldChange={(subQuestionId, value) => {
            // Update the editable form data with the new sub-answers
            const updatedObjectData = {
              ...currentObjectData,
              [subQuestionId]: value,
            }
            handleFieldChange(fieldKey, updatedObjectData)
          }}
        />
      )
    }

    // For non-object questions, show them based on deletion status
    // Show logic:
    // 1. If question is NOT deleted: show it always
    // 2. If question IS deleted: show it ONLY if it has answers (for users who answered before deletion)
    const shouldShowQuestion = isDeleted
      ? hasValue(currentValue) // Show deleted questions only if they have answers
      : true // Show non-deleted questions always

    if (!shouldShowQuestion) {
      return null
    }

    // Handle different question types
    if (answerType === 'singleSelectionDropdown') {
      // Debug: Check if displayValue matches any option
      const availableOptions =
        options?.map((option) => {
          const optionValue =
            typeof option === 'object' && option !== null
              ? option.option || option.text || ''
              : option
          return optionValue
        }) || []

      const hasMatchingOption = availableOptions.includes(displayValue)

      return (
        <div className='mb-4' key={`${fieldKey}-single-selection`}>
          <label className='s10a order-0 mb-12px'>
            {renderLabel(question, isRequired, links)}
          </label>
          <select
            className={classNames('form-control', {
              'is-invalid': validationErrors[fieldKey] && isEditable,
            })}
            value={displayValue}
            disabled={!isEditable}
            onChange={
              isEditable
                ? (e) => {
                    // For dependent question logic, we need to store both the option text and ID
                    const selectedOptionText = e.target.value

                    // If no option is selected (empty string), store null to indicate no selection
                    if (
                      !selectedOptionText ||
                      selectedOptionText.trim() === ''
                    ) {
                      handleFieldChange(fieldKey, null)
                      return
                    }

                    const selectedOption = options?.find((opt) => {
                      const optText = typeof opt === 'object' ? opt.option : opt
                      return optText === selectedOptionText
                    })
                    const selectedOptionId =
                      selectedOption?._id || selectedOption?.id

                    // Store the option ID in a special format for dependent logic
                    const valueWithId = selectedOptionId
                      ? `${selectedOptionText}|${selectedOptionId}`
                      : selectedOptionText
                    handleFieldChange(fieldKey, valueWithId)
                  }
                : undefined
            }
            style={{
              backgroundColor: '#fff',
              minHeight: '42px',
              height: 'auto',
            }}>
            {!isEditable && !hasMatchingOption && (
              <option value='' disabled>
                {displayValue}
              </option>
            )}
            {isEditable && <option value=''>Select an option</option>}
            {options?.map((option, optionIndex) => {
              // Handle both string and object options
              const optionValue =
                typeof option === 'object' && option !== null
                  ? option.option || option.text || ''
                  : option
              const optionKey = `${fieldKey}-option-${optionIndex}`

              return (
                <option key={optionKey} value={optionValue}>
                  {optionValue}
                </option>
              )
            })}
          </select>
        </div>
      )
    }

    if (answerType === 'multipleSelectionDropdown') {
      const selectedValues = Array.isArray(currentValue)
        ? currentValue.filter((val) => val && val.trim() !== '')
        : currentValue && currentValue.trim() !== ''
        ? [currentValue]
        : []

      return (
        <div className='mb-4' key={`${fieldKey}-multiple-selection`}>
          <label className='s10a order-0 mb-12px'>
            {renderLabel(question, isRequired, links)}
          </label>
          {isEditable ? (
            <div className='checkbox-group'>
              {options?.map((option, optionIndex) => {
                // Handle both string and object options
                const optionValue =
                  typeof option === 'object' && option !== null
                    ? option.option || option.text || ''
                    : option
                const optionKey = `${fieldKey}-option-${optionIndex}`

                return (
                  <FormCheckbox
                    key={optionKey}
                    checkboxId={`${fieldKey}-${optionValue}`}
                    isChecked={selectedValues.includes(optionValue)}
                    onChange={() => {
                      if (selectedValues.includes(optionValue)) {
                        const newValues = selectedValues.filter(
                          (val) => val !== optionValue,
                        )
                        handleFieldChange(fieldKey, newValues)
                      } else {
                        const newValues = [...selectedValues, optionValue]
                        handleFieldChange(fieldKey, newValues)
                      }
                    }}
                    label={optionValue}
                    disabled={!isEditable}
                  />
                )
              })}
            </div>
          ) : (
            <input
              className='form-input-display form-control'
              value={
                selectedValues.length > 0
                  ? selectedValues.join(', ')
                  : 'No options selected'
              }
              readOnly
              disabled
            />
          )}
        </div>
      )
    }

    if (answerType === 'date') {
      const dateValue = displayValue || ''
      return (
        <div className='mb-4' key={`${fieldKey}-date`}>
          <label className='s10a order-0 mb-12px'>
            {renderLabel(question, isRequired, links)}
          </label>
          {isEditable ? (
            <SimpleDatePicker
              value={dateValue}
              onChange={(date) => {
                handleFieldChange(fieldKey, date)
              }}
              disabled={!isEditable}
              inputStyle={{
                background: '#fff',
                height: '42px',
              }}
              placeholder='MM/DD/YYYY'
            />
          ) : (
            <input
              className='form-input-display form-control'
              value={displayValue}
              readOnly
              disabled
            />
          )}
        </div>
      )
    }

    if (answerType === 'scale') {
      const scaleOptionsLimit = scaleOptions?.maxScaleLimit || 6
      return (
        <div className='mb-4' key={`${fieldKey}-scale`}>
          <label className='s10a order-0 mb-12px'>
            {renderLabel(question, isRequired, links)}
          </label>
          <select
            className={classNames('form-control', {
              'is-invalid': validationErrors[fieldKey] && isEditable,
            })}
            value={displayValue}
            disabled={!isEditable}
            onChange={
              isEditable
                ? (e) => handleFieldChange(fieldKey, e.target.value)
                : undefined
            }
            style={{
              backgroundColor: '#fff',
              minHeight: '42px',
              height: 'auto',
            }}>
            <option value=''>
              {isEditable ? 'Select a value' : displayValue}
            </option>
            {Array.from({ length: scaleOptionsLimit }, (_, i) => i + 1).map(
              (value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ),
            )}
          </select>
        </div>
      )
    }

    // Show country field
    if (answerType === 'country') {
      return (
        <div className='mb-4' key={`${fieldKey}-country`}>
          <label className='s10a order-0 mb-12px'>
            {renderLabel(question, isRequired, links)}
          </label>
          {isEditable ? (
            <CountryInput
              value={displayValue || ''}
              onChange={(value) => handleFieldChange(fieldKey, value)}
              isRequired={isRequired}
              disabled={!isEditable}
              placeholder='Select a country'
              hasError={!!validationErrors[fieldKey]}
            />
          ) : (
            <div className='d-flex align-items-center'>
              {displayValue ? (
                (() => {
                  // Extract country info from display value
                  const countryMatch = displayValue.match(/^\+(\d+)\s+(.+)$/)
                  let country = null
                  let countryName = ''
                  let dialCode = ''

                  if (countryMatch) {
                    dialCode = countryMatch[1]
                    countryName = countryMatch[2]

                    // Find country by dial code and name
                    const countryData = getProcessedCountryData()
                    country = countryData.find(
                      (c) => c.dialCode === dialCode && c.name === countryName,
                    )
                  } else {
                    // Fallback: when only country name is saved (legacy), try to match by name
                    const countryData = getProcessedCountryData()
                    country = countryData.find(
                      (c) =>
                        (displayValue || '').toLowerCase() ===
                        c.name.toLowerCase(),
                    )
                    if (country) {
                      countryName = country.name
                      dialCode = country.dialCode
                    }
                  }

                  return (
                    <>
                      {/* Country selector display */}
                      <div
                        className='me-2 d-flex align-items-center justify-content-between'
                        style={{
                          minWidth: '100px',
                          border: '1px solid #000',
                          background: '#f8f9fa',
                          height: '42px',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: 'default',
                        }}>
                        <ReactCountryFlag
                          countryCode={(country?.code || 'us').toUpperCase()}
                          svg
                          className='me-2'
                          style={{
                            width: '1.25em',
                            height: '1.25em',
                            borderRadius: '30%',
                          }}
                          aria-label={(country?.code || 'us').toUpperCase()}
                        />
                        <span className='text-truncate answer-dial-code'>
                          +{dialCode || '1'}
                        </span>
                      </div>

                      {/* Country name display */}
                      <input
                        className='form-control flex-grow-1'
                        value={countryName || displayValue || ''}
                        readOnly
                        disabled
                        style={{
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #000',
                          color: '#000',
                          height: '42px',
                          fontSize: '14px',
                        }}
                      />
                    </>
                  )
                })()
              ) : (
                <>
                  {/* Empty country selector display */}
                  <div
                    className='me-2 d-flex align-items-center justify-content-between'
                    style={{
                      minWidth: '100px',
                      border: '1px solid #000',
                      background: '#f8f9fa',
                      height: '42px',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      cursor: 'default',
                    }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>
                      Select
                    </span>
                  </div>

                  {/* Empty country name display */}
                  <input
                    className='form-control flex-grow-1'
                    value=''
                    readOnly
                    disabled
                    placeholder='No country selected'
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #000',
                      color: '#666',
                      height: '42px',
                      fontSize: '14px',
                    }}
                  />
                </>
              )}
            </div>
          )}
        </div>
      )
    }

    // Show phone number without flag
    if (
      question &&
      typeof question === 'string' &&
      question.toLowerCase().includes('phone')
    ) {
      // Extract country and phone for read-only display
      const extractCountryAndPhoneForDisplay = (savedValue) => {
        if (!savedValue || typeof savedValue !== 'string') {
          return { countryCode: 'us', phoneNumber: '' }
        }

        // New format: +{dialCode}|{countryCode} {phoneNumber}
        const newFormatMatch = savedValue.match(/^\+(\d+)\|([a-z]{2})\s+(.+)$/)
        if (newFormatMatch) {
          const countryCode = newFormatMatch[2]
          const phoneNumber = newFormatMatch[3]
          return { countryCode, phoneNumber }
        }

        // Legacy format: +{dialCode} {phoneNumber} (for backward compatibility)
        const legacyMatch = savedValue.match(/^\+(\d+)\s+(.+)$/)
        if (legacyMatch) {
          const dialCode = legacyMatch[1]
          const phoneNumber = legacyMatch[2]

          // Find country by dial code - prefer US for dial code 1
          if (dialCode === '1') {
            return { countryCode: 'us', phoneNumber }
          }

          const countryByDialCode = getCountryByDialCode(dialCode)
          if (countryByDialCode) {
            return { countryCode: countryByDialCode.code, phoneNumber }
          }
        }

        return { countryCode: 'us', phoneNumber: savedValue }
      }

      const { countryCode, phoneNumber } =
        extractCountryAndPhoneForDisplay(displayValue)

      // Get country data for display
      const countryData = getProcessedCountryData()
      const selectedCountry = countryData.find((c) => c.code === countryCode) ||
        countryData.find((c) => c.code === 'us') || {
          flag: '🇺🇸',
          dialCode: '1',
        }

      return (
        <div className='mb-4' key={`${fieldKey}-phone`}>
          <label className='s10a order-0 mb-12px'>
            {renderLabel(question, isRequired, links)}
          </label>
          {isEditable ? (
            <PhoneInput
              value={displayValue || ''}
              onChange={(value) => handleFieldChange(fieldKey, value)}
              isRequired={isRequired}
              disabled={!isEditable}
              placeholder='Enter phone number'
              hasError={!!validationErrors[fieldKey]}
            />
          ) : (
            <div className='d-flex align-items-center'>
              {/* Country display */}
              <div
                className='me-2 d-flex align-items-center justify-content-between'
                style={{
                  minWidth: '100px',
                  border: '1px solid #000',
                  background: '#f8f9fa',
                  height: '42px',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  cursor: 'default',
                }}>
                <ReactCountryFlag
                  countryCode={(selectedCountry?.code || 'us').toUpperCase()}
                  svg
                  className='me-2'
                  style={{
                    width: '1.25em',
                    height: '1.25em',
                    borderRadius: '30%',
                  }}
                  aria-label={(selectedCountry?.code || 'us').toUpperCase()}
                />
                <span className='text-truncate answer-dial-code'>
                  +{selectedCountry.dialCode}
                </span>
              </div>

              {/* Phone number display */}
              <input
                className='form-control flex-grow-1'
                value={phoneNumber || ''}
                readOnly
                disabled
                style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #000',
                  color: '#000',
                  height: '42px',
                  fontSize: '14px',
                }}
              />
            </div>
          )}
        </div>
      )
    }

    // Use textarea for text-type questions, input for others
    if (answerType === 'text') {
      // Use the same currentValue logic as other fields for consistency
      const textValue = currentValue

      return (
        <div className='mb-4' key={`${fieldKey}-text`}>
          <label className='s10a order-0 mb-12px'>
            {renderLabel(question, isRequired, links)}
          </label>
          {isEditable ? (
            // Editable textarea with auto-resize
            <textarea
              className={classNames('form-textarea-display form-control', {
                'is-invalid': validationErrors[fieldKey] && isEditable,
              })}
              value={textValue || ''}
              onChange={(e) => {
                handleFieldChange(fieldKey, e.target.value)
                // Auto-resize the textarea to fit content
                const textarea = e.target
                textarea.style.height = 'auto'
                textarea.style.height = textarea.scrollHeight + 'px'
              }}
              onFocus={(e) => {
                // Auto-resize on focus to fit content
                const textarea = e.target
                textarea.style.height = 'auto'
                textarea.style.height = textarea.scrollHeight + 'px'
              }}
              onKeyDown={(e) => {
                // Allow Enter key for new lines, but prevent form submission
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                }
              }}
              rows={1}
              placeholder='Enter your answer here... (Press Shift + Enter for new lines)'
              style={{
                resize: 'vertical',
                overflow: 'auto',
                lineHeight: '1.5',
                fontFamily: 'inherit',
                backgroundColor: '#fff',
              }}
            />
          ) : (
            // Read-only textarea with fixed height based on content
            <textarea
              className='form-textarea-display form-control'
              value={textValue || ''}
              readOnly
              disabled
              rows={1}
              style={{
                resize: 'none',
                overflow: 'hidden',
                lineHeight: '1.5',
                fontFamily: 'inherit',
                backgroundColor: '#f8f9fa',
                cursor: 'default',
              }}
            />
          )}
        </div>
      )
    }

    return (
      <div className='mb-4' key={`${fieldKey}-default`}>
        <label className='s10a order-0 mb-12px'>
          {renderLabel(question, isRequired, links)}
        </label>
        <input
          className={classNames('form-input-display form-control', {
            'is-invalid': validationErrors[fieldKey] && isEditable,
          })}
          value={displayValue || ''}
          readOnly={!isEditable}
          disabled={!isEditable}
          autoComplete='off'
          onChange={
            isEditable
              ? (e) => handleFieldChange(fieldKey, e.target.value)
              : undefined
          }
          style={{
            backgroundColor: '#fff',
            height: '42px',
          }}
        />
      </div>
    )
  }

  return (
    <>
      <PageHeader
        showBackArrow={true}
        version='small'
        backArrowAction={() => history.goBack()}
        title={'Your Experiences' || questTitle}
      />

      <div className='container mt-3'>
        <Row>
          <div className='col-lg-12 col-md-12 col-sm-12'>
            {isLoading || isUpdating ? (
              <div className='enroll'>
                {[...Array(6)].map((_, index) => (
                  <div className='mt-4' key={index}>
                    <div className='row align-items-center'>
                      <div className='col-8 col-md-5 col-lg-5'>
                        <ShimmerThumbnail line={1} height={30} />
                      </div>
                      <div className='col-12'>
                        <div className='mt-2'>
                          <ShimmerThumbnail line={1} height={50} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='enroll-form-answer'>
                <div className='d-flex flex-lg-row flex-column justify-content-lg-between align-items-lg-center mb-3'>
                  <h1 className='s1 mb-lg-0 mb-3'>
                    {questTitle || 'Enrollment Form Answer'}
                  </h1>
                  <div className='d-flex gap-2 justify-content-end'>
                    {isEditable && (
                      <Button
                        // variant='secondary'
                        size='md'
                        className={classNames(
                          'text-uppercase line-height-normal payment-back-btn s9 border-radius-normal',
                          {
                            'pe-none opacity-50': isUpdating,
                            'cursor-pointer': !isUpdating,
                          },
                        )}
                        onClick={() => {
                          if (!isUpdating) {
                            handleCancel()
                          }
                        }}>
                        Cancel
                      </Button>
                    )}
                    <Button
                      type='submit'
                      variant='saffron'
                      className={classNames(
                        'text-uppercase line-height-normal s9 border-radius-normal',
                        {
                          'pe-none opacity-50': isUpdating,
                          'cursor-pointer': !isUpdating,
                        },
                      )}
                      size='md'
                      onClick={() => {
                        if (!isEditable) {
                          setIsEditable(true)
                        } else {
                          onSubmit()
                        }
                      }}>
                      {isEditable ? 'Save' : isUpdating ? 'Saving...' : 'EDIT'}
                    </Button>
                  </div>
                </div>
                <div className='my-info px-0'>
                  {filteredAnswers
                    ?.filter((answer) => {
                      // If answerType is 'files', filter out
                      return answer.answerType !== 'files'
                    })
                    .map((answer, index) => {
                      try {
                        return renderField(answer, index)
                      } catch (error) {
                        Error.showError(error)
                        return null
                      }
                    })}
                </div>
              </div>
            )}
          </div>
        </Row>
      </div>
    </>
  )
}

export default EnrollTypeFormAnswer
